import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { usd } from "../lib/format";
import { Icon } from "../components/icons";
import { Empty } from "../components/ui";
import type { HealthModel, HealthBand } from "../lib/types";
import { applyHealthModel, DEFAULT_HEALTH_MODEL, normalizeModel } from "../lib/healthmodel";
import * as work from "../lib/workClient";

/* Health Score Designer — a no-code way to define what "healthy" means.
 * Drag the weight on each factor, toggle factors on or off, and watch the
 * whole book re-score live: the band distribution shifts, at-risk ARR moves,
 * and every account's projected health updates against the standard model.
 * Save to make it the workspace's model. */

const bandColor: Record<HealthBand, string> = { good: "var(--good)", watch: "var(--watch)", risk: "var(--risk)" };
const bandLabel: Record<HealthBand, string> = { good: "Healthy", watch: "Penumbra", risk: "At risk" };

function eqModel(a: HealthModel, b: HealthModel) {
  return JSON.stringify(a.factors) === JSON.stringify(b.factors);
}

export default function HealthDesigner() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [saved, setSaved] = useState<HealthModel>(DEFAULT_HEALTH_MODEL);
  const [model, setModel] = useState<HealthModel>(DEFAULT_HEALTH_MODEL);
  const [busy, setBusy] = useState(false);

  useEffect(() => { work.loadHealthModel(ws).then((r) => { setMode(r.mode); setSaved(r.model); setModel(r.model); }); }, [ws]);

  // live, instant preview computed locally (byte-compatible with the API)
  const impact = useMemo(() => applyHealthModel(ws, model), [ws, model]);
  const dirty = !eqModel(model, saved);
  const totalWeight = model.factors.filter((f) => f.enabled).reduce((s, f) => s + f.weight, 0);

  const setWeight = (label: string, weight: number) =>
    setModel((m) => ({ factors: m.factors.map((f) => f.label === label ? { ...f, weight } : f) }));
  const toggle = (label: string) =>
    setModel((m) => ({ factors: m.factors.map((f) => f.label === label ? { ...f, enabled: !f.enabled } : f) }));

  const save = async () => { setBusy(true); const r = await work.saveHealthModel(mode, ws, normalizeModel(model)); setSaved(r.model); setModel(r.model); setBusy(false); };

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Configure</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Health Score Designer</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Define what "healthy" means for your book. Re-weight the factors, toggle any of them off, and watch the whole portfolio re-score live against the standard model before you commit.</p>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <span className={`mode-pill ${mode === "api" ? "live" : ""}`}><i />{mode === "api" ? "Live API" : "Local demo"}</span>
          <button className="btn btn--ghost btn--sm" onClick={() => setModel(DEFAULT_HEALTH_MODEL)} disabled={eqModel(model, DEFAULT_HEALTH_MODEL)}>Reset to default</button>
          <button className="btn btn--primary btn--sm" onClick={save} disabled={!dirty || busy}>{busy ? "Saving…" : dirty ? "Save model" : "Saved"}</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(340px, 420px) 1fr", gap: 20, alignItems: "start" }}>
        {/* ---- factor weights ---- */}
        <div className="card">
          <div className="card__hd"><div><h3>Scoring factors</h3><span className="sub">Weights normalize to 100%</span></div></div>
          <div className="card__bd" style={{ display: "grid", gap: 18 }}>
            {model.factors.map((f) => {
              const share = f.enabled && totalWeight ? Math.round((f.weight / totalWeight) * 100) : 0;
              return (
                <div key={f.label} style={{ opacity: f.enabled ? 1 : 0.5 }}>
                  <div className="row between" style={{ marginBottom: 6 }}>
                    <label className="row" style={{ gap: 8, fontSize: 14, fontWeight: 600 }}>
                      <input type="checkbox" checked={f.enabled} onChange={() => toggle(f.label)} />
                      {f.label}
                    </label>
                    <span className="tag">{f.enabled ? `${share}%` : "off"}</span>
                  </div>
                  <input type="range" min={0} max={100} value={f.weight} disabled={!f.enabled}
                    onChange={(e) => setWeight(f.label, Number(e.target.value))} style={{ width: "100%", accentColor: "var(--corona)" }} />
                  <div className="faint" style={{ fontSize: 11.5, marginTop: 2 }}>Raw weight {f.weight}</div>
                </div>
              );
            })}
            {totalWeight === 0 && <div className="badge badge--risk">Enable at least one factor</div>}
          </div>
        </div>

        {/* ---- live impact ---- */}
        <div style={{ display: "grid", gap: 20 }}>
          <div className="stats" style={{ marginBottom: 0 }}>
            <div className="stat"><div className="stat__label">Avg health</div><div className="stat__value">{impact.summary.avgProjected}<small style={{ color: impact.summary.avgDelta === 0 ? "var(--faint)" : impact.summary.avgDelta > 0 ? "var(--good)" : "var(--risk)" }}> {impact.summary.avgDelta >= 0 ? "+" : ""}{impact.summary.avgDelta}</small></div><div className="stat__foot">was {impact.summary.avgCurrent}</div></div>
            <div className="stat"><div className="stat__label">Accounts re-banded</div><div className="stat__value" style={{ color: impact.summary.moved ? "var(--watch)" : "var(--faint)" }}>{impact.summary.moved}</div><div className="stat__foot">cross a band boundary</div></div>
            <div className="stat"><div className="stat__label">At-risk ARR</div><div className="stat__value">{usd(impact.summary.atRiskArrProjected)}</div><div className="stat__foot">was {usd(impact.summary.atRiskArrCurrent)}</div></div>
            <div className="stat"><div className="stat__label">Band mix</div><div className="row" style={{ gap: 6, marginTop: 8 }}>{(["good", "watch", "risk"] as HealthBand[]).map((b) => <span key={b} className="badge" style={{ color: bandColor[b], background: "var(--surface-2)" }}>{impact.summary.projectedBands[b]}</span>)}</div><div className="stat__foot">healthy · penumbra · risk</div></div>
          </div>

          <div className="card">
            <div className="card__hd"><div><h3>Projected impact</h3><span className="sub">Every account, standard model → your model · biggest drops first</span></div></div>
            <div style={{ overflowX: "auto" }}>
              {impact.rows.length === 0 ? <div className="card__bd"><Empty>No accounts to score.</Empty></div> : (
                <table className="table">
                  <thead><tr><th>Account</th><th style={{ textAlign: "right" }}>ARR</th><th style={{ textAlign: "right" }}>Standard</th><th style={{ textAlign: "right" }}>Your model</th><th style={{ textAlign: "right" }}>Δ</th><th>Band</th></tr></thead>
                  <tbody>
                    {impact.rows.map((r) => (
                      <tr key={r.accountId}>
                        <td><Link to={`/accounts/${r.accountId}`} style={{ fontWeight: 600 }}>{r.accountName}</Link></td>
                        <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{usd(r.arr)}</td>
                        <td style={{ textAlign: "right", color: "var(--faint)", fontVariantNumeric: "tabular-nums" }}>{r.current}</td>
                        <td style={{ textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{r.projected}</td>
                        <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: r.delta === 0 ? "var(--faint)" : r.delta > 0 ? "var(--good)" : "var(--risk)" }}>{r.delta > 0 ? "+" : ""}{r.delta}</td>
                        <td>
                          {r.currentBand === r.projectedBand
                            ? <span className="badge" style={{ color: bandColor[r.projectedBand], background: "var(--surface-2)" }}>{bandLabel[r.projectedBand]}</span>
                            : <span className="row" style={{ gap: 5, fontSize: 12 }}><span style={{ color: "var(--faint)" }}>{bandLabel[r.currentBand]}</span><Icon name="arrowRight" size={12} /><b style={{ color: bandColor[r.projectedBand] }}>{bandLabel[r.projectedBand]}</b></span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
